import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';

let fallbackHref = '/';
let baseHref: string;

/** Regex associated with url */
export const urlRegex = {
  httpAbsolute: /^[a-z][a-z0-9+.-]*:\/\//i,
  dataUrl: /^data:/i,
  blobUrl: /^blob:/i,
};

/** Adds base href url as prefix to absolute paths */
export function prefixUrl(
  logicalPath: string | undefined | null,
  servePath: string = getBaseHref(),
): string {
  // Validate path, prefix and guard against http/data Url
  if (!servePath) return logicalPath || '';
  if (typeof logicalPath !== 'string') return servePath;
  if (isExceptedUrl(logicalPath)) return logicalPath;
  // Normalize prefix. Skip if prefix is already there
  const base = normalizeServePath(servePath);
  if (base && logicalPath.startsWith(base)) return logicalPath;
  // Generate path with prefix
  const path = logicalPath.replace(/^\/+/, '');
  return base ? `${base}/${path}` : `/${path}`;
}

function isExceptedUrl(logicalPath: string): boolean {
  const exceptedReg = [urlRegex.httpAbsolute, urlRegex.dataUrl, urlRegex.blobUrl];
  if (exceptedReg.some((reg) => reg.test(logicalPath))) return true;
  return false;
}

/** Cleans and normalizes serve path */
function normalizeServePath(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (!trimmed || trimmed === '/') {
    return '';
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/** Local base href, to allow use outside of injection context */
function getBaseHref(): string {
  if (baseHref) return baseHref;
  return baseHrefFallback();
}

/** Get base href directly from document */
function baseHrefFallback(): string {
  try {
    return document.querySelector('base')?.getAttribute('href') || '/';
  } catch {
    return fallbackHref || '/';
  }
}

/** Explicitly bind APP_BASE_HREF for angular and prefixUrl base href */
export function provideBaseHref(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_BASE_HREF,
      useFactory: (platformLocation: PlatformLocation) => {
        baseHref = platformLocation.getBaseHrefFromDOM();
        return baseHref;
      },
      deps: [PlatformLocation],
    },
  ]);
}
